import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Printer, Download, FileText, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import PdfCanvas from './PdfCanvas';

/**
 * Shows the HP label and hands it to the browser for printing or saving.
 *
 * Two separate things have to work here, and on mobile neither did. The preview
 * used an <iframe> pointed at a blob: URL, which no phone can render (see
 * PdfCanvas); Print and Download drove that same blob: URL, and a WebView has
 * neither a tab to open one in nor permission to download one. Both now go
 * through a real, signed PDF URL that the OS viewer can take over.
 */
export default function LabelPreviewModal({ isOpen, onClose, labelData, orderName }) {
  const base64 = labelData?.labelPdfBase64 || null;
  const barcode = labelData?.barcode || null;
  const [links, setLinks] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [renderFailed, setRenderFailed] = useState(false);

  const bytes = useMemo(() => {
    if (!base64) return null;
    try {
      const binary = atob(base64);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
      return out;
    } catch {
      return null;
    }
  }, [base64]);

  const decodeError = Boolean(base64) && !bytes;

  useEffect(() => {
    setRenderFailed(false);
  }, [bytes]);

  /**
   * Fetched when the modal opens, not when a button is clicked: minting the
   * link is async, and iOS refuses a window.open that no longer sits inside the
   * user gesture that started it.
   */
  useEffect(() => {
    if (!isOpen || !barcode) {
      setLinks(null);
      setLinkError(null);
      return undefined;
    }
    let cancelled = false;
    api
      .getLabelPdfUrl(barcode)
      .then(({ url, downloadUrl }) => {
        if (!cancelled) setLinks({ url, downloadUrl });
      })
      .catch((err) => {
        console.error('[label] Could not mint a PDF link:', err);
        // Surfaced rather than swallowed: otherwise Print and Download just sit
        // there greyed out with nothing saying why.
        if (!cancelled) setLinkError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, barcode]);

  const handleRenderError = useCallback(() => setRenderFailed(true), []);

  if (!isOpen || !labelData) return null;

  const { direction } = labelData;
  // Archive rows created before shipping labels existed carry no direction, and
  // every one of those is a return.
  const outbound = direction === 'OUTBOUND';
  const title = outbound ? 'HP shipping label' : 'HP return label';
  const showPreview = Boolean(bytes) && !renderFailed;

  let emptyMessage = 'The label exists at Hrvatska Pošta but no PDF is stored yet.';
  if (decodeError) emptyMessage = 'The label PDF returned by Hrvatska Pošta could not be read.';
  else if (renderFailed) emptyMessage = 'The label PDF could not be drawn in the browser.';

  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ maxWidth: '780px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#e6f4ea', color: '#137333', padding: '0.4rem', borderRadius: '50%', display: 'flex' }}>
              <CheckCircle2 size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{title}</h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-secondary)' }}>
                Order {orderName} • Barcode{' '}
                <strong style={{ color: 'var(--color-brand-hp)' }}>{barcode}</strong>
              </p>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '0.4rem' }}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ background: '#1e1e1e', padding: '1.25rem' }}>
          {showPreview ? (
            <PdfCanvas bytes={bytes} onError={handleRenderError} />
          ) : (
            <div className="pdf-preview-box">
              <FileText size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
              <p>{emptyMessage}</p>
              <p style={{ fontSize: '0.85rem', color: '#aaa', marginTop: '0.5rem' }}>Barcode: {barcode}</p>
              <p style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.5rem' }}>
                {links
                  ? 'Open it below to view it outside the app, or find it in the HP portal.'
                  : 'Open it from the Labels Archive to retry the download, or find it in the HP portal.'}
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {linkError && (
            <p className="modal-footer-note">
              Print and download are unavailable: {linkError}
            </p>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {/* Plain anchors, not window.open: a real link is the one navigation a
              mobile WebView reliably hands to the system PDF viewer. */}
          <a
            className={`btn btn-secondary${links ? '' : ' btn-disabled'}`}
            href={links?.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!links}
          >
            <Download size={16} /> Download PDF
          </a>
          <a
            className={`btn btn-hp${links ? '' : ' btn-disabled'}`}
            href={links?.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!links}
          >
            <Printer size={16} /> Print label
          </a>
        </div>
      </div>
    </div>
  );
}
